import { useSyncExternalStore } from "react";

/** The browser's offer to install the app, held until the practice takes it: Chrome and Edge, on Android and computers. */
type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

let offered: InstallEvent | null = null;
const listeners = new Set<() => void>();

function changed() {
  for (const listener of listeners) {
    listener();
  }
}

function isInstallEvent(event: Event): event is InstallEvent {
  return "prompt" in event && typeof event.prompt === "function" && "userChoice" in event;
}

/** Listened from the first load: the browser makes its offer once, early, whatever screen is open. */
export function listenForInstall(target: Window = window) {
  target.addEventListener("beforeinstallprompt", (event) => {
    if (isInstallEvent(event)) {
      event.preventDefault();
      offered = event;
      changed();
    }
  });
  target.addEventListener("appinstalled", () => {
    offered = null;
    changed();
  });
}

/** How the app can be installed here: by the browser's offer, by hand on an iPhone or iPad, or not at all (already
 * installed, or a browser that offers nothing). */
export type InstallWay = "offer" | "ios" | "none";

function standalone(): boolean {
  const iosStandalone = Reflect.get(navigator, "standalone") === true;
  return (
    iosStandalone ||
    (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches)
  );
}

function apple(): boolean {
  const agent = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(agent) || (agent.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

function wayNow(): InstallWay {
  if (standalone()) {
    return "none";
  }
  if (offered !== null) {
    return "offer";
  }
  return apple() ? "ios" : "none";
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The way to install the app, kept up to date as the browser makes or withdraws its offer. */
export function useInstallWay(): InstallWay {
  return useSyncExternalStore(subscribe, wayNow, () => "none");
}

/** Shows the browser's own install dialog; tells whether the practice accepted. */
export async function install(): Promise<boolean> {
  const event = offered;
  if (event === null) {
    return false;
  }
  await event.prompt();
  const { outcome } = await event.userChoice;
  offered = null;
  changed();
  return outcome === "accepted";
}

const dismissedKey = "instarchi.install.dismissed";

/** Whether the practice set the offer aside on this device; a storage the browser refuses counts as not. */
export function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(dismissedKey) === "1";
  } catch {
    return false;
  }
}

export function rememberDismissed() {
  try {
    window.localStorage.setItem(dismissedKey, "1");
  } catch {
    // The offer comes back next time: nothing is lost.
  }
}
